import { BANK_CHARGES_NOTICE } from '../invest-v2/invest-v2-share-order'
import React, { useEffect, useMemo, useState } from 'react'
import './payment.scss'
import {
  H2,
  Span,
  Anchor,
  H4,
  Image,
  Line,
  InputFile,
  PendingIcon,
  Button,
  Input,
  H5,
  H6,
} from '../../lib/components'
import { displayDollar } from '../../lib/utils/helpers'
import bankDetails from './bank-details'
import { ENABLE_PAYMENT_PROMO_CODE, PLATFORM_FEE_CURRENCY } from './constants'
import { FILE_UPLOAD_PURPOSE, PaymentUpload } from '../../lib/types'
import { investServices } from '../../services'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { InvestmentFeeSummary } from '../../services/types'
import { renderFeeRow } from './DisplayFee'
import { dispatchDealingChargesWaivedBannerIfNeeded } from './dealing-charges-waived-banner'
import { InvestV2WireTransferCard } from '../invest-v2/invest-v2-wire-transfer-card'

const totalAmountToTransferUsd = (summary: InvestmentFeeSummary | undefined): number => {
  if (!summary) return 0
  const inv = summary.investmentAmount ?? 0
  const list = summary.fees ?? []
  const dealing = list.find((f) => f.name === 'platformFee')?.appliedAmount ?? 0
  const otherFees = list
    .filter((f) => f.name !== 'platformFee')
    .reduce((acc, f) => acc + (f.appliedAmount ?? 0), 0)
  return inv + otherFees + dealing
}

const PromoCodeSection = () => {
  const [promoCode, setPromoCode] = useState('')
  return (
    <>
      <Line />
      <div className='property-row'>
        <span className='property-column-left' data-testid='promo-code-label'>
          Promo code (optional):
        </span>
      </div>
      <Input
        type='text'
        name='promoCode'
        placeholder='Enter code if you have one'
        data-testid='investment-promo-code-input'
        value={promoCode}
        handleChange={(e) => setPromoCode(e.target.value)}
        errors={{}}
        touched={{ promoCode: false }}
      />
      <Line />
    </>
  )
}

const PaymentUploadDocument = ({
  opportunity,
  investment,
  handleFileUpload,
  handleCompletePayment,
  hideTransferHeading,
  onFundLater,
  completePaymentLabel,
  onBack,
}: PaymentUpload) => {
  const dispatch = useDispatch()
  const { authToken } = useSelector((state: RootState) => state.investor)
  const [currency, setCurrency] = useState<'USD' | 'AED'>('USD')
  const [paymentCopy, setPaymentCopy] = useState(
    investment ? investment?.metadata?.paymentCopy : undefined,
  )
  const [transactionNumber, setTransactionNumber] = useState<string>('')
  const fileData = {
    name: 'Payment copy',
    status: 'UPLOADED',
    lastModified:
      investment && investment.updated_at && new Date(investment.updated_at).getMilliseconds(),
  }
  const fileInfo = paymentCopy && paymentCopy.length > 0 ? fileData : []
  const hasPaymentCopy = Boolean(paymentCopy && paymentCopy.length > 0)
  const trimmedTxn = transactionNumber.trim()
  const hasValidTransactionRef =
    trimmedTxn.length >= 10 && trimmedTxn.length <= 20 && /^[A-Za-z0-9- ]+$/.test(trimmedTxn)
  const isDisabled = !hasPaymentCopy && !hasValidTransactionRef
  const [investmentFeeSummary, setinvestmentFeeSummary] = useState<InvestmentFeeSummary>()
  const opportunityId = investment?.opportunity_id || 0
  // Share-priced orders carry cents (e.g. a 5.55% fee), so keep them visible.
  const showCents = Boolean(investment?.shares)
  const investmentAmount = investment?.amount || '0'
  const investmentId = investment?.id
  const platformFeeRow = investmentFeeSummary?.fees?.find((f) => f.name === 'platformFee')
  const computedTotalTransfer = useMemo(
    () => totalAmountToTransferUsd(investmentFeeSummary),
    [investmentFeeSummary],
  )
  const handleFileChange = async (file: any) => {
    if (!file) return
    const response =
      handleFileUpload && (await handleFileUpload(file as File, FILE_UPLOAD_PURPOSE.PAYMENT_COPY))
    if (response && response.status === 200) {
      setPaymentCopy(response.data.key)
    }
  }

  const submitCompletedTransfer = () => {
    if (isDisabled || !handleCompletePayment) return
    handleCompletePayment(paymentCopy ?? '', transactionNumber)
  }

  const handleTransactionNumberErrorMessage = (val: string): { transactionNumber?: string } => {
    const transactionNumber = val.trim()

    if (transactionNumber === '') {
      return {
        transactionNumber: 'Transaction number cannot be empty',
      }
    }

    const regex = /^[A-Za-z0-9- ]+$/
    if (!regex.test(transactionNumber)) {
      return {
        transactionNumber: 'Transaction number contains invalid characters',
      }
    }

    if (transactionNumber.length < 10) {
      return {
        transactionNumber: 'Transaction number must be more than 10 characters',
      }
    }

    if (transactionNumber.length > 20) {
      return {
        transactionNumber: 'Transaction number must be less than 20 characters',
      }
    }

    return {}
  }

  const transactionFieldErrors = hasPaymentCopy
    ? {}
    : handleTransactionNumberErrorMessage(transactionNumber)

  useEffect(() => {
    const getInvestmentFeeSummary = async () => {
      if (!investmentId) return
      const data = await investServices.getInvestmentFeeSummary(
        {
          opportunityId,
          investmentAmount: (Number(investmentAmount) / 100).toString(),
          investmentId,
        },
        {
          Authorization: authToken ? authToken : '',
        },
      )
      dispatchDealingChargesWaivedBannerIfNeeded(dispatch, data)
      setinvestmentFeeSummary(data)
    }
    getInvestmentFeeSummary()
  }, [opportunityId, investmentAmount, investmentId, authToken])

  return (
    <>
      {!hideTransferHeading && (
        <div className='payment-upload-doc-heading-container'>
          <H2 text='Transfer funds' />
          <div>
            <Span text={'Payment process for'} />
            <Anchor
              classes={['invest-header-project-title']}
              text={opportunity.projectName}
              href={`/opportunity/${opportunity?.projectSlug}/${opportunity?.id}`}
              variant='basic-anchor'
            />
          </div>
        </div>
      )}
      <div
        data-testid='invest-funds-transfer-payment-warning'
        className='payment-warning-container-full mt-12 mb-12'
      >
        <PendingIcon classes={['info-icon']} width='24' height='24' />
        Please note that clients are responsible for bearing all SWIFT charges related to their
        transactions. Credibila will not cover or reimburse SWIFT charges under any circumstances.
        When initiating a wire transfer, please ensure you select the appropriate options to bear
        the SWIFT charges yourself.
      </div>
      <div className='payment-upload-doc-user-wrapper'>
        <div className='payment-upload-doc-user-wrapper-left'>
          {!hideTransferHeading ? (
            <H4 classes={['h4-heading']} text='Wire transfer details' />
          ) : null}

          {!hideTransferHeading ? (
            <div className='currency-toggle'>
              <button
                type='button'
                className={`currency-toggle__btn ${
                  currency === 'USD' ? 'currency-toggle__btn--active' : ''
                }`}
                onClick={() => setCurrency('USD')}
              >
                USD
              </button>

              <button
                type='button'
                className={`currency-toggle__btn ${
                  currency === 'AED' ? 'currency-toggle__btn--active' : ''
                }`}
                onClick={() => setCurrency('AED')}
              >
                AED
              </button>
            </div>
          ) : null}

          {hideTransferHeading && investment ? (
            <InvestV2WireTransferCard opportunity={opportunity} investment={investment} />
          ) : (
            <div className='payment-upload-doc-user-container'>
              <table className='payment-upload-doc-user-table'>
                <thead>
                  <tr>
                    <th>DETAILS</th>
                    <th>{currency}</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(bankDetails.wireTransferDetails).map((k) => {
                    const item =
                      bankDetails.wireTransferDetails[
                        k as keyof typeof bankDetails.wireTransferDetails
                      ]
                    return (
                      <tr key={k}>
                        <td>{item.title}</td>
                        <td>{currency === 'USD' ? item.usdValue : item.aedValue}</td>
                      </tr>
                    )
                  })}
                  <tr className='payment-upload-doc-user-table-row__caution-row'>
                    <td>PURPOSE OF TRANSACTION:</td>
                    <td>Investment through Credibila Markets</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <div className='acknowledgement-container'>
            <H4 text='Swift copy or bank acknowledgement' />
            <span className='sub-heading'>
              Upload your payment slip or enter your transaction reference below — only one is
              required.
            </span>
            <InputFile
              setRemoveFile={() => setPaymentCopy('')}
              type='doc'
              label=''
              testId='invest-upload-file-input'
              name='swiftCopyBank'
              handleFiles={handleFileChange}
              fileInfo={fileInfo}
            />
            <H5 text='OR' classes={['align-center', 'mt-20', 'mb-20']} />
            <H6 text='Enter Transaction Reference Number:' classes={['mb-12']} />
            <Input
              type='text'
              name='transactionNumber'
              placeholder='Transaction Reference Number'
              data-testid='transaction number-input'
              value={transactionNumber}
              handleChange={(e) => {
                setTransactionNumber(e.target.value)
              }}
              errors={transactionFieldErrors}
              touched={{
                transactionNumber: transactionNumber.length > 0 && !hasPaymentCopy,
              }}
              infoText={
                'If you are not uploading a slip, enter a valid transaction reference between 10 and 20 characters (letters, numbers, spaces, or hyphens).'
              }
            />
          </div>
          <div
            className='payment-warning-container'
            data-testid='invest-funds-transfer-decision-allocation-warning'
          >
            <PendingIcon classes={['info-icon']} width='24' height='24' />
            The investment is subject to allocation decision of the fund manager and availability of
            allocation
          </div>
          <div
            className={`payment-button-container${
              onBack ? ' payment-button-container--with-back' : ''
            }`}
          >
            {onBack ? (
              <>
                <button
                  type='button'
                  className='payment-upload-back-link'
                  onClick={onBack}
                  data-testid='payment-upload-back'
                >
                  ← Back
                </button>
                <div className='payment-button-container__trailing'>
                  {onFundLater && (
                    <Button
                      text={"I'll do it later"}
                      variant='basic-btn'
                      handleClick={onFundLater}
                      testId='payment-upload-fund-later'
                    />
                  )}
                  <Button
                    text={completePaymentLabel ?? 'Complete payment'}
                    isDisabled={isDisabled}
                    handleClick={submitCompletedTransfer}
                    variant='primary-btn'
                    testId='payment-upload-complete'
                  />
                </div>
              </>
            ) : (
              <>
                {onFundLater && (
                  <Button
                    text={"I'll do it later"}
                    variant='basic-btn'
                    handleClick={onFundLater}
                    testId='payment-upload-fund-later'
                  />
                )}
                <Button
                  text={completePaymentLabel ?? 'Complete payment'}
                  isDisabled={isDisabled}
                  handleClick={submitCompletedTransfer}
                  variant='primary-btn'
                  testId='payment-upload-complete'
                />
              </>
            )}
          </div>
        </div>
        <div className='payment-upload-doc-user-wrapper-right'>
          <div className='payment-company-card'>
            <span className='summary-label'> {'Summary'} </span>
            <div className='company-image-container'>
              <Image source={opportunity.projectCover} radius='r8' variant='round' />
              <span className='company-name'> {opportunity.projectName} </span>
            </div>
            <div className='property-container'>
              <div className='property-row'>
                <span className='property-column-left' data-testid='investment-amount-text'>
                  Investment Amount:
                </span>
                <span className='property-column-right' data-testid='investment-amount-value'>
                  {displayDollar(
                    ((investmentFeeSummary?.investmentAmount || 0) * 100).toString(),
                    opportunity.currencyCode,
                    2,
                    true,
                    showCents,
                  )}
                </span>
              </div>
              <div className='property-row'>
                <span className='property-column-left'>
                  (This will be invested in {opportunity?.projectName})
                </span>
              </div>

              <Line />
              <div className='property-row'>
                <span className='property-column-left bold'>Fees</span>
              </div>
              {investmentFeeSummary?.fees?.length ? (
                <>
                  {investmentFeeSummary.fees
                    .filter((fee) => {
                      return fee.name !== 'platformFee'
                    })
                    .map((fee) => {
                      return renderFeeRow(fee, opportunity.currencyCode || '', showCents)
                    })}
                </>
              ) : null}
              <div className='property-row'>
                <span className='property-column-left' data-testid='platform-fee-text'>
                  Dealing Charges:
                </span>
                <span className='property-column-right' data-testid='platform-fee-value'>
                  {displayDollar(
                    ((platformFeeRow?.appliedAmount || 0) * 100).toString(),
                    PLATFORM_FEE_CURRENCY,
                  )}
                </span>
              </div>
              {ENABLE_PAYMENT_PROMO_CODE ? <PromoCodeSection /> : <Line />}
              <div className='property-row'>
                <span className='property-column-left' data-testid='total-amount-transfered-text'>
                  Total Amount To Be Transferred:
                </span>
                <span className='property-column-right' data-testid='total-amount-transfered-value'>
                  {displayDollar(
                    (computedTotalTransfer * 100).toString(),
                    opportunity.currencyCode,
                    2,
                    true,
                    showCents,
                  )}
                </span>
              </div>
              {investment?.shares ? (
                <p className='iv2-bank-purpose-note' data-testid='bank-charges-notice'>
                  {BANK_CHARGES_NOTICE}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export { PaymentUploadDocument }
